import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

import { RouteParamsResolverService } from './core/services/route-params-resolver.service';
import { DashboardComponent } from './dashboard/dashboard.component';
import { OfflineDownloadsOverviewComponent } from './side-navigation/offline-downloads-overview/offline-downloads-overview.component';
import { MsalGuard } from '@azure/msal-angular';
import { HeaderComponent } from './header';
import { HealthComponent } from './health/health.component';
import { ReportsComponent } from './reports/reports.component';
import { VendorComponent } from './vendor/vendor.component';
import { SystemOverviewComponent } from './system-overview/system-overview.component';
import { ProjectOverviewComponent } from './project-overview/project-overview.component';
import { RoadmapGuard } from './core/guards/roadmap.guard';
import { CoreDataGuard } from './core/guards/coreData.guard';
import { AdminGuard } from './core/guards/admin.guard';
import { NoProjectAccessComponent } from './no-project-access/no-project-access.component';
import { UiShowcaseComponent } from './shared/ui/ui-showcase/ui-showcase.component';
import { ServiceUnavailableComponent } from './service-unavailable/service-unavailable.component';
import { AuthGuard } from './core/guards/auth.guard';

const routes: Routes = [
  {
    path: 'service-unavailable',
    canActivate: [MsalGuard],
    component: ServiceUnavailableComponent,
    data: { title: 'Service Unavailable' },
  },
  {
    path: 'no-project-access',
    canActivate: [MsalGuard],
    component: NoProjectAccessComponent,
    data: { title: 'No Project Access' },
  },
  {
    path: 'admin',
    canActivate: [MsalGuard, CoreDataGuard, AdminGuard],
    loadChildren: () => import('./admin/admin.module').then((m) => m.AdminModule),
    data: { title: 'Admin', hideMainSideNav: true },
  },
  {
    path: '',
    canActivate: [CoreDataGuard],
    children: [
      { path: 'vendor', component: VendorComponent, loadChildren: () => import('./vendor/vendor.module').then((m) => m.VendorModule) },
      {
        path: 'system/:systemId',
        component: SystemOverviewComponent,
        loadChildren: () => import('./system-overview/system-overview.module').then((m) => m.SystemOverviewModule),
      },

      {
        path: 'health',
        component: HealthComponent,
      },
      {
        path: 'callback',
        component: HeaderComponent,
      },
      {
        path: '',
        canActivate: [MsalGuard],
        component: DashboardComponent,
        pathMatch: 'full',
      },
      {
        path: 'dashboard',
        canActivate: [MsalGuard],
        component: DashboardComponent,
        data: {
          title: 'Dashboard',
        },
      },
      {
        path: 'offline',
        canActivate: [MsalGuard],
        component: DashboardComponent,
        data: {
          title: 'Offline',
        },
      },
      {
        path: 'supplier',
        canActivate: [MsalGuard, AuthGuard],
        loadChildren: () => import('./supplier/supplier.module').then((m) => m.SupplierModule),
        data: {
          title: 'Supplier',
        },
      },
      {
        path: 'heat-trace',
        canActivate: [MsalGuard],
        loadChildren: () => import('./heat-trace/heat-trace.module').then((m) => m.HeatTraceModule),
        data: {
          title: 'Heat Trace',
        },
      },
      {
        path: 'batch',
        canActivate: [MsalGuard],
        loadChildren: () => import('./batch/batch.module').then((m) => m.BatchModule),
        data: {
          title: 'Batch Processing',
        },
      },
      {
        path: 'downloads',
        canActivate: [MsalGuard],
        component: OfflineDownloadsOverviewComponent,
        data: {
          title: 'Downloads',
        },
      },
      {
        path: 'reports',
        canActivate: [MsalGuard, AuthGuard],
        component: ReportsComponent,
        data: {
          title: 'Downloads',
        },
      },
      {
        path: 'project/:projectId/object/:objectId',
        canActivate: [MsalGuard],
        loadChildren: () => import('./mips-landing/mips-landing.module').then((m) => m.MipsLandingModule),
        data: { title: 'MIPS Checksheets' },
      },
      {
        path: 'cp',
        canActivate: [MsalGuard],
        loadChildren: () => import('./commissioning-package/commissioning-package.module').then((m) => m.CommissioningPackageModule),
      },
      {
        path: 'cp/:id',
        loadChildren: () => import('./cp-overview/cp-overview.module').then((m) => m.CpOverviewModule),
        resolve: {
          cp: RouteParamsResolverService,
        },
      },
      {
        path: 'cp/:id/mcp/:mcpId',
        loadChildren: () => import('./mc-overview/mc-overview.module').then((m) => m.McOverviewModule),
        resolve: {
          cp: RouteParamsResolverService,
        },
      },
      {
        path: 'cp/:id/checksheet/:csId',
        loadChildren: () => import('./check-sheet/check-sheet.module').then((m) => m.CheckSheetModule),
        resolve: {
          cs: RouteParamsResolverService,
        },
        data: {
          title: 'CP Checksheet',
        },
      },
      {
        path: 'cp/:id/mcp/:mcpId/checksheet/:csId',
        loadChildren: () => import('./check-sheet/check-sheet.module').then((m) => m.CheckSheetModule),
        resolve: {
          cs: RouteParamsResolverService,
        },
        data: {
          title: 'MCP Checksheet',
        },
      },
      {
        path: 'cp/:id/certificates',
        loadChildren: () => import('./certificates/certificates.module').then((m) => m.CertificatesModule),
        resolve: {
          cs: RouteParamsResolverService,
        },
      },
      {
        path: 'cp/:id/handover-docs',
        loadChildren: () => import('./handover-docs/handover-docs.module').then((m) => m.HandoverDocsModule),
        resolve: {
          cs: RouteParamsResolverService,
        },
      },
      {
        path: 'project-overview',
        canActivate: [MsalGuard, AuthGuard],
        component: ProjectOverviewComponent,
        data: { title: 'Project Overview' },
      },
      {
        path: 'roadmap',
        canActivate: [MsalGuard, RoadmapGuard],
        loadChildren: () => import('./roadmap/roadmap.module').then((m) => m.RoadmapModule),
        data: { title: 'Roadmap' },
      },
    ],
  },
  {
    path: 'ui-showcase',
    component: UiShowcaseComponent,
    data: { title: 'UI Showcase' },
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { bindToComponentInputs: true })],
  exports: [RouterModule],
})
export class AppRoutingModule {}
